/**
 * IndexedDB service for storing large image files
 * This avoids localStorage quota limitations
 */

const DB_NAME = 'WeddieQuestionnaire';
const DB_VERSION = 1;
const IMAGES_STORE = 'images';

// Storage context type - used to separate questionnaire and editor data
export type StorageContext = 'questionnaire' | 'editor';

// Image types
export enum ImageKey {
    DESKTOP_IMAGE = 'desktopImage',
    MOBILE_IMAGE = 'mobileImage',
    WITNESS1_IMAGE = 'witness1Image',
    WITNESS2_IMAGE = 'witness2Image'
}

/**
 * Creates a contextual key by prefixing with storage context
 * This ensures questionnaire and editor images are stored separately
 * @param key - The base image key
 * @param context - The storage context (questionnaire or editor)
 * @returns Contextual key string (e.g., 'questionnaire_desktopImage')
 */
const getContextualKey = (key: ImageKey, context: StorageContext): string => {
    return `${context}_${key}`;
};

/**
 * Opens or creates the IndexedDB database
 */
const openDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => {
            reject(new Error('Failed to open IndexedDB'));
        };
        
        request.onsuccess = () => {
            resolve(request.result);
        };
        
        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            
            // Create object store if it doesn't exist
            if (!db.objectStoreNames.contains(IMAGES_STORE)) {
                db.createObjectStore(IMAGES_STORE);
            }
        };
    });
};

/**
 * Saves an image to IndexedDB
 * @param key - The image key (e.g., 'desktopImage')
 * @param imageData - Base64 encoded image data
 * @param context - Storage context (questionnaire or editor), defaults to questionnaire for backward compatibility
 */
export const saveImage = async (
    key: ImageKey, 
    imageData: string,
    context: StorageContext = 'questionnaire'
): Promise<void> => {
    try {
        const db = await openDB();
        const transaction = db.transaction([IMAGES_STORE], 'readwrite');
        const store = transaction.objectStore(IMAGES_STORE);
        
        const contextualKey = getContextualKey(key, context);
        const request = store.put(imageData, contextualKey);
        
        return new Promise((resolve, reject) => {
            request.onsuccess = () => {
                console.log(`Image saved to IndexedDB: ${contextualKey} (${(imageData.length / 1024).toFixed(2)} KB)`);
                resolve();
            };
            
            request.onerror = () => {
                reject(new Error(`Failed to save image: ${contextualKey}`));
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        });
    } catch (error) {
        console.error(`Error saving image to IndexedDB: ${key}`, error);
        throw error;
    }
};

/**
 * Gets an image from IndexedDB
 * @param key - The image key
 * @param context - Storage context (questionnaire or editor), defaults to questionnaire for backward compatibility
 * @returns Base64 encoded image data or null if not found
 */
export const getImage = async (
    key: ImageKey,
    context: StorageContext = 'questionnaire'
): Promise<string | null> => {
    try {
        const db = await openDB();
        const transaction = db.transaction([IMAGES_STORE], 'readonly');
        const store = transaction.objectStore(IMAGES_STORE);
        
        const contextualKey = getContextualKey(key, context);
        const request = store.get(contextualKey);
        
        return new Promise((resolve, reject) => {
            request.onsuccess = () => {
                const result = request.result || null;
                if (result) {
                    console.log(`Image loaded from IndexedDB: ${contextualKey} (${(result.length / 1024).toFixed(2)} KB)`);
                }
                resolve(result);
            };
            
            request.onerror = () => {
                reject(new Error(`Failed to get image: ${contextualKey}`));
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        });
    } catch (error) {
        console.error(`Error getting image from IndexedDB: ${key}`, error);
        return null;
    }
};

/**
 * Deletes an image from IndexedDB
 * @param key - The image key
 * @param context - Storage context (questionnaire or editor), defaults to questionnaire for backward compatibility
 */
export const deleteImage = async (
    key: ImageKey,
    context: StorageContext = 'questionnaire'
): Promise<void> => {
    try {
        const db = await openDB();
        const transaction = db.transaction([IMAGES_STORE], 'readwrite');
        const store = transaction.objectStore(IMAGES_STORE);
        
        const contextualKey = getContextualKey(key, context);
        const request = store.delete(contextualKey);
        
        return new Promise((resolve, reject) => {
            request.onsuccess = () => {
                console.log(`Image deleted from IndexedDB: ${contextualKey}`);
                resolve();
            };
            
            request.onerror = () => {
                reject(new Error(`Failed to delete image: ${contextualKey}`));
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        });
    } catch (error) {
        console.error(`Error deleting image from IndexedDB: ${key}`, error);
        throw error;
    }
};

/**
 * Gets all images from IndexedDB for a specific storage context
 * @param context - Storage context (questionnaire or editor), defaults to questionnaire for backward compatibility
 * @returns Object with all images for the specified context
 */
export const getAllImages = async (
    context: StorageContext = 'questionnaire'
): Promise<Record<string, string>> => {
    try {
        const db = await openDB();
        const transaction = db.transaction([IMAGES_STORE], 'readonly');
        const store = transaction.objectStore(IMAGES_STORE);
        
        const request = store.getAll();
        const keysRequest = store.getAllKeys();
        
        return new Promise((resolve, reject) => {
            const results: Record<string, string> = {};
            const contextPrefix = `${context}_`;
            
            request.onsuccess = () => {
                const values = request.result;
                const keys = keysRequest.result;
                
                // Filter keys that match the context prefix
                keys.forEach((key, index) => {
                    const keyStr = key as string;
                    if (keyStr.startsWith(contextPrefix)) {
                        results[keyStr] = values[index];
                    }
                });
                
                console.log(`Loaded ${Object.keys(results).length} images from IndexedDB for context: ${context}`);
                resolve(results);
            };
            
            request.onerror = () => {
                reject(new Error('Failed to get all images'));
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        });
    } catch (error) {
        console.error('Error getting all images from IndexedDB:', error);
        return {};
    }
};

/**
 * Clears all images from IndexedDB for a specific storage context
 * @param context - Storage context (questionnaire or editor), defaults to questionnaire for backward compatibility
 */
export const clearAllImages = async (
    context: StorageContext = 'questionnaire'
): Promise<void> => {
    try {
        const db = await openDB();
        const transaction = db.transaction([IMAGES_STORE], 'readwrite');
        const store = transaction.objectStore(IMAGES_STORE);
        
        // Get all keys and delete only those matching the context
        const keysRequest = store.getAllKeys();
        
        return new Promise((resolve, reject) => {
            keysRequest.onsuccess = () => {
                const keys = keysRequest.result;
                const contextPrefix = `${context}_`;
                let deleteCount = 0;
                
                // Delete each key that matches the context
                keys.forEach((key) => {
                    const keyStr = key as string;
                    if (keyStr.startsWith(contextPrefix)) {
                        store.delete(key);
                        deleteCount++;
                    }
                });
                
                console.log(`Cleared ${deleteCount} images from IndexedDB for context: ${context}`);
                resolve();
            };
            
            keysRequest.onerror = () => {
                reject(new Error('Failed to clear images'));
            };
            
            transaction.oncomplete = () => {
                db.close();
            };
        });
    } catch (error) {
        console.error('Error clearing images from IndexedDB:', error);
        throw error;
    }
};
